import moment from "moment";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { startSearchLoading } from "shared/data/actions/loader";
import { AppState } from "shared/data/reducers";
import { useLowCostSearch } from "shared/hooks/lowCostSearch";
import { setInitialRequestorConfig } from "shared/lib/requestorFunctions";
import { formattedMessageParams } from "shared/messages";
// @ts-ignore
import Logger from "shared/services/Logger";
import { SpecialOfferParameters } from "shared/types";

import Itinerary from "@components/BestOffer/BestOfferItinerary";
import Passengers from "@components/BestOffer/BestOfferPassengers";
import Calendar from "@components/LowCostSearch/Calendar";
import SelectFlightsModal from "@components/LowCostSearch/SelectFlightsModal";
import Title from "@components/LowCostSearch/Title";
import FormattedMessage from "@components/UI/FormattedMessage";
import StandardLayout from "@components/UI/Layouts/StandardLayout";
import PrimaryButton from "@components/UI/PrimaryButton";
import PageSeo from "@components/Utils/PageSeo";

const MAX_CALENDARS = 6;

const { searchTitle } = defineMessages({
  searchTitle: formattedMessageParams("Iframe.title"),
});

function LowCostSearch() {
  const router = useRouter();
  const { query } = router;
  const intl = useIntl();
  const dispatch = useDispatch();
  const language = useSelector(
    (state: AppState) => state.requestorConfig.currentLanguage
  );
  const [isActiveSelectFlightsModal, setActiveSelectFlightsModal] = useState(
    false
  );

  const { from: origin, to: destination } = query as {
    from: string;
    to: string;
  };

  const specialOfferParameters: SpecialOfferParameters = {
    origin,
    destination,
    type: "return",
  };

  const {
    enabled,
    passengers,
    addPassenger,
    removePassenger,
    flightsLoaded,
    getFirstDayOfMonthOffset,
    getFlights,
    selectDay,
    selectedDays,
    getSelectedFlightsTotalPrice,
    firstAvailableMonthOffset,
  } = useLowCostSearch({
    origin,
    destination,
  });

  useEffect(() => {
    if (!enabled) {
      router.push({
        pathname: "/404",
      });
    }
  }, [enabled, router]);

  const renderCalendar = (params: {
    origin: string;
    destination: string;
    monthOffset: number;
    flightDirection: "departure" | "return";
  }) => {
    const flights = getFlights(
      params.origin,
      params.destination,
      params.monthOffset
    );
    return (
      <Calendar
        id={`calendar-${params.monthOffset}`}
        locale={language}
        firstAvailableDate={
          new Date(getFirstDayOfMonthOffset(params.monthOffset))
        }
        flights={flights}
        onDayClick={selectDay}
        flightDirection={params.flightDirection}
        selectedDays={selectedDays}
      />
    );
  };

  const handleConfirmSelectFlights = () => {
    setActiveSelectFlightsModal(false);
    dispatch(startSearchLoading());
    router.push({
      pathname: "/results",
      query: {
        from: origin,
        to: destination,
        flightClass: "ECO",
        departureDate: moment(selectedDays.from).format("YYYY-MM-DD"),
        returnDate: moment(selectedDays.to).format("YYYY-MM-DD"),
        ...Object.keys(passengers).reduce(
          (acc, curr) => ({
            ...acc,
            ...(passengers[curr] > 0 ? { [curr]: passengers[curr] } : {}),
          }),
          {}
        ),
      },
    });
  };

  const renderCalendars = () => {
    if (!flightsLoaded) {
      return <></>;
    }

    if (firstAvailableMonthOffset === undefined) {
      return (
        <div>
          <FormattedMessage id="LowCostSearch.noFlightsAvailable" />
        </div>
      );
    }

    return Array.from({ length: MAX_CALENDARS }).map((_, index) => {
      const monthOffset = firstAvailableMonthOffset + index;
      return (
        <React.Fragment key={`calendar-${monthOffset}`}>
          {renderCalendar({
            origin,
            destination,
            monthOffset,
            flightDirection: "departure",
          })}
          {renderCalendar({
            origin: destination,
            destination: origin,
            monthOffset,
            flightDirection: "return",
          })}
        </React.Fragment>
      );
    });
  };

  const isSelectFlightsButtonDisabled = () => {
    return !selectedDays.from || !selectedDays.to;
  };

  const renderContentPage = () => {
    if (!enabled) {
      return <></>;
    }

    return (
      <>
        <SelectFlightsModal
          isActive={isActiveSelectFlightsModal}
          onClose={() => setActiveSelectFlightsModal(false)}
          onConfirm={() => handleConfirmSelectFlights()}
          selectedDays={selectedDays}
          totalPrice={getSelectedFlightsTotalPrice()}
        />
        <div className="universal-content-box">
          <div className="low-cost-search-options">
            <Itinerary offerParameters={specialOfferParameters} />
            <Passengers
              passengers={passengers}
              addPassenger={addPassenger}
              removePassenger={removePassenger}
            />
          </div>
          <div className="low-cost-search-select-flight">
            {flightsLoaded && firstAvailableMonthOffset !== undefined && (
              <PrimaryButton
                onClick={() => setActiveSelectFlightsModal(true)}
                className={isSelectFlightsButtonDisabled() ? "disabled" : ""}
              >
                <FormattedMessage id="LowCostSearch.selectFlight" />
              </PrimaryButton>
            )}
          </div>
          <div className="low-cost-search-way-there">
            <Title />
            <div className="low-cost-search-calendars-container">
              {renderCalendars()}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <PageSeo
        title={{
          defaultTitle: intl.formatMessage(searchTitle),
        }}
      />
      <style global jsx>{`
        html,
        body {
          background: transparent !important;
        }
      `}</style>

      {renderContentPage()}
    </>
  );
}

LowCostSearch.getLayout = (page, nonce) => (
  <StandardLayout nonce={nonce} hideSearchForm hideFooter>
    {page}
  </StandardLayout>
);

LowCostSearch.getInitialProps = async (initialProps) => {
  try {
    const { domain } = await setInitialRequestorConfig(initialProps);
    return { domain };
  } catch (e) {
    Logger.error(`Error Initial index: ${JSON.stringify(e)}`);
    return {};
  }
};

export default LowCostSearch;
